import * as Yup from 'yup';
import { getFormattedDateYmd } from '../../../global/helpers/date-helpers';
import { getRequiredText } from '../../../global/helpers/transformer';
import { YesNoIndicators } from '../../../global/models/api-model';
import { LabelSettingProps } from '../../../global/models/epi-model';
import { ClergyBioSettings } from '../../arc/models/arc-epi-props';
import { ClergyBioMinistry } from '../model/clergy-bio-app-models';
import { ClergyBioMinistriesFormModel } from './models/form-models';


export function GetClergyBioMinistriesValidationSchema(settings: ClergyBioSettings, labelSetting: LabelSettingProps) {

	const position = Yup.object().shape({
		position: Yup.string().when(['delete'], {
			is: (a) => a !== YesNoIndicators.Yes,
			then: Yup.string().required(getRequiredText(labelSetting.requiredFormat, settings.positionTitleLabel)),
		}),
		dateBegan: Yup.date().nullable().when(['delete'], {
			is: (a) => a !== YesNoIndicators.Yes,
			then: Yup.date().nullable().required(getRequiredText(labelSetting.requiredFormat, settings.positionStartDateLabel)),
		}),
		dateEnded: Yup.date().nullable(),
	});

	const ministry = Yup.object().shape({
		institutionParsed: Yup.string().when(['delete'], {
			is: (a) => a !== YesNoIndicators.Yes,
			then: Yup.string().required(getRequiredText(labelSetting.requiredFormat, settings.miscellaneousInstitutionLabel)),			
		}),
		dateBegan: Yup.date().nullable().when(['delete'], {
			is: (a) => a !== YesNoIndicators.Yes,
			then: Yup.date().nullable().required(getRequiredText(labelSetting.requiredFormat, settings.employmentStartDateLabel)),
		}),
		positions: Yup.array().nullable().when(['delete'], {
			is: (a) => a !== YesNoIndicators.Yes,
			then: Yup.array().nullable().of(position),
		}),
		dateEnded: Yup.date().nullable(),		
		institutionInfo: Yup.object().nullable().when(['delete'], {
			is: (a) => a !== YesNoIndicators.Yes,
			then: Yup.object().nullable()
			.test('employer_id_verification', 
			(value, context) => {
				if (!value?.id){
					return context.createError({ 
						path: context.path.replace('institutionInfo', 'institutionParsed'),
						message: getRequiredText(labelSetting.requiredFormat, settings.miscellaneousInstitutionLabel), 
					});
				}

				return true;
			}),
		}),		
	});

	const miscellaneous = Yup.object().shape({
		dateBegan: Yup.date().nullable().when(['delete'], {
			is: (a) => a !== YesNoIndicators.Yes,
			then: Yup.date().nullable().required(getRequiredText(labelSetting.requiredFormat, settings.positionStartDateLabel)),
		}),
		dateEnded: Yup.date().nullable(),
		institutionInfo: Yup.string().when(['delete'], {
			is: (a) => a !== YesNoIndicators.Yes,
			then: Yup.string().required(getRequiredText(labelSetting.requiredFormat, settings.miscellaneousInstitutionLabel)),
		}),
		position: Yup.array().nullable().when(['delete'], {
			is: (a) => a !== YesNoIndicators.Yes,
			then: Yup.array().nullable().of(position),
		}),
	});

	return Yup.object().shape({
		stipendiary: Yup.array().of(ministry),
		nonStipendiary: Yup.array().of(ministry),
		miscellaneous: Yup.array().of(miscellaneous),
	});
}

function shouldKeep(item: ClergyBioMinistry) {
	return (item.id || (!item?.id && item?.deleted !== YesNoIndicators.Yes)) && item?.positions?.length > 0;
}

export function ConstructPayload(values: ClergyBioMinistriesFormModel) {
	return {
		ministry: {
			employments: values?.stipendiary?.filter(a => shouldKeep(a))?.map(employment => {

				const stipendiaryPayload =
				{
					hire_date: getFormattedDateYmd(employment.dateBegan, true),
					termination_date: getFormattedDateYmd(employment.dateEnded, true),
					id: employment?.id,
					employer_id: employment?.institutionInfo?.id,
					position_titles: employment?.positions?.filter(a => a.id || (!a?.id && a?.deleted !== YesNoIndicators.Yes))?.map(position => {
						const positionPayload = {
							id: position?.id,
							published_title: position?.publishedPosition,
							position_title: position?.position,
							start_date: getFormattedDateYmd(position?.dateBegan, true),
							end_date: getFormattedDateYmd(position?.dateEnded, true),
						};

						return position?.deleted ? {
							...positionPayload,
							deleted: position?.deleted,
						} : positionPayload;
					}),
				};

				return employment?.deleted ? {
					...stipendiaryPayload,
					deleted: employment?.deleted,
				} : stipendiaryPayload;
			}),
			non_stipendiary_positions: values?.nonStipendiary?.filter(a => shouldKeep(a))?.map(employment => {

				const nonStipPayload = {
					hire_date: getFormattedDateYmd(employment.dateBegan, true),
					termination_date: getFormattedDateYmd(employment.dateEnded, true),
					id: employment.id,
					employer_id: employment?.institutionInfo?.id,
					position_titles: employment?.positions?.filter(a => a.id || (!a?.id && a?.deleted !== YesNoIndicators.Yes))?.map(position => {
						const positionPayload = {
							id: position?.id,
							published_title: position?.publishedPosition,
							position_title: position?.position,
							start_date: getFormattedDateYmd(position?.dateBegan, true),
							end_date: getFormattedDateYmd(position?.dateEnded, true),
						};
						return position?.deleted ? {
							...positionPayload,
							deleted: position?.deleted,
						} : positionPayload;
					}),
				};

				return employment?.deleted ? {
					...nonStipPayload,
					deleted: employment?.deleted,
				} : nonStipPayload;
			}),
			miscellaneous: values?.miscellaneous?.filter(a => a.id || (!a?.id && a?.deleted !== YesNoIndicators.Yes))?.map(employment => {
				const miscellaneousPayload = {
					id: employment?.id,
					employer_name: employment?.institutionInfo,
					position_title: employment?.position,
					position_start_date: getFormattedDateYmd(employment?.dateBegan, true),
					position_end_date: getFormattedDateYmd(employment?.dateEnded, true),
				};

				return employment?.deleted ? {
					...miscellaneousPayload,
					deleted: employment?.deleted,
				} : miscellaneousPayload;
			}),
		},
	};
}

export function createNewPosition() {
	return {
		position: '',
		publishedPosition: '',
		dateBegan: null,
		dateEnded: null,
		deleted: '',
	};
}

export function createNewMinistry() {
	return {
		dateBegan: null,
		dateEnded: null,
		deleted: '',
		institutionInfo: {
			id: '',
			name: '',
			location: '',
		},
		positions: [],
		institutionParsed: '',
	};
}
export function createNewMiscellaneous() {
	return {
		dateBegan: null,
		dateEnded: null,
		institutionInfo: '',
		position: '',
		deleted: '',
	};
}



